/**
 * /group/approvals — cross-business expense approval queue.
 *
 * The "CFO over GH₵5k" demo flagship.
 *
 * Access: tenant_owner, super_admin, business_admin.
 *   • Tenant-level users see every submitted expenditure across all
 *     businesses in the tenant.
 *   • business_admin sees only the businesses they are actively assigned
 *     to (and only items below threshold for them to approve — though
 *     items above threshold are visible for context).
 *
 * Threshold (default GH₵5,000): expenditures at or above require
 * tenant_owner or super_admin approval. business_admins get a clear
 * "Requires CFO" badge on those items.
 *
 * Day 14 §5.3.2 Group Approvals.
 */
import { useState, useMemo } from "react";
import {
  ShieldCheck,
  CircleDollarSign,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Building2,
  Filter,
  Settings,
  Loader2,
  Receipt,
  Paperclip,
  RefreshCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  useApprovalQueue,
  useApprovalThreshold,
  useApproveExpenditure,
  useRejectExpenditure,
  useSetThreshold,
} from "@/hooks/useGroupApprovals";
import { useMyBusinesses } from "@/hooks/useBusinesses";
import useAuthStore from "@/stores/authStore";
import type { ApprovalQueueItem } from "@/types/group-approvals.types";
import { curSymbol, formatMajor } from "@/lib/currency";

// ─── Helpers ────────────────────────────────────────────────────────

function normalizeBusinesses(
  raw: unknown,
): Array<{ _id: string; name: string }> {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((b) => {
      const o = b as Record<string, unknown>;
      const _id =
        (o._id as string) || (o.id as string) || (o.businessId as string) || "";
      const name =
        (o.name as string) || (o.businessName as string) || "Unnamed";
      return _id ? { _id, name } : null;
    })
    .filter((x): x is { _id: string; name: string } => Boolean(x))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function formatGHS(amount: number): string {
  return formatMajor(amount || 0);
}

function relativeDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const min = 60_000,
    hr = 60 * min,
    day = 24 * hr;
  if (diff < min) return "just now";
  if (diff < hr) return `${Math.floor(diff / min)}m ago`;
  if (diff < day) return `${Math.floor(diff / hr)}h ago`;
  if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

// ─── Page ────────────────────────────────────────────────────────────

export default function GroupApprovalsPage() {
  const user = useAuthStore((s) => s.user);
  const [businessFilter, setBusinessFilter] = useState<string>("");
  const [escalatedOnly, setEscalatedOnly] = useState(false);

  // Reject dialog state
  const [rejectTarget, setRejectTarget] = useState<ApprovalQueueItem | null>(
    null,
  );
  const [rejectReason, setRejectReason] = useState("");

  // Threshold dialog state
  const [thresholdOpen, setThresholdOpen] = useState(false);
  const [thresholdInput, setThresholdInput] = useState("");

  const { data: businessesRaw } = useMyBusinesses();
  const businesses = useMemo(
    () => normalizeBusinesses(businessesRaw),
    [businessesRaw],
  );

  const { data, isLoading, isError, refetch, isFetching } = useApprovalQueue({
    businessId: businessFilter || undefined,
    escalatedOnly,
  });
  const { data: thresholdData } = useApprovalThreshold();

  const approve = useApproveExpenditure();
  const reject = useRejectExpenditure();
  const setThreshold = useSetThreshold();

  const items = data?.items || [];
  const totals = data?.totals;
  const threshold = data?.threshold ?? thresholdData?.threshold ?? 5000;

  const canEditThreshold = user?.role === "tenant_owner";

  async function onApprove(item: ApprovalQueueItem) {
    try {
      await approve.mutateAsync(item.expenditure._id);
      toast.success(`Approved · ${formatGHS(item.expenditure.amount)}`);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || "Failed to approve");
    }
  }

  async function onRejectSubmit() {
    if (!rejectTarget) return;
    if (!rejectReason.trim()) {
      toast.error("Reason is required");
      return;
    }
    try {
      await reject.mutateAsync({
        expenditureId: rejectTarget.expenditure._id,
        reason: rejectReason.trim(),
      });
      toast.success("Rejected");
      setRejectTarget(null);
      setRejectReason("");
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || "Failed to reject");
    }
  }

  function openThresholdDialog() {
    setThresholdInput(String(threshold));
    setThresholdOpen(true);
  }

  async function onSaveThreshold() {
    const n = Number(thresholdInput);
    if (!Number.isFinite(n) || n < 0) {
      toast.error("Threshold must be a non-negative number");
      return;
    }
    try {
      await setThreshold.mutateAsync(n);
      toast.success(`Threshold set to ${formatGHS(n)}`);
      setThresholdOpen(false);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || "Failed to update");
    }
  }

  return (
    <div className="min-h-screen bg-background">
      {/* ─── Hero ─── */}
      <div className="bg-gradient-to-br from-[#0A2540] to-[#0E2C44] text-white relative overflow-hidden">
        <div
          className="absolute -top-20 right-1/4 h-40 w-[36rem] rounded-full bg-[#4CAF50]/20 blur-3xl"
          aria-hidden
        />
        <div className="max-w-6xl mx-auto px-6 py-8 relative">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-[#7BAEC8] mb-1">
                <ShieldCheck className="h-3 w-3" />
                Group approvals
              </div>
              <h1 className="text-2xl md:text-3xl font-semibold text-white">
                Pending approvals
              </h1>
              <p className="text-sm text-[#A0BBCB] mt-1">
                Every business, one queue. Approve or reject with one click.
              </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <div className="rounded-md border border-white/15 bg-white/5 px-3 py-2">
                <div className="text-[10px] font-medium uppercase tracking-wider text-[#A0BBCB]">
                  CFO threshold
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-base font-semibold tabular-nums">
                    {formatGHS(threshold)}
                  </span>
                  {canEditThreshold && (
                    <button
                      type="button"
                      onClick={openThresholdDialog}
                      className="text-[#7BAEC8] hover:text-white transition-colors"
                      aria-label="Edit threshold"
                    >
                      <Settings className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => refetch()}
                disabled={isFetching}
                className="text-white/80 hover:text-white hover:bg-white/15"
              >
                <RefreshCcw
                  className={`h-3.5 w-3.5 mr-2 ${
                    isFetching ? "animate-spin" : ""
                  }`}
                />
                Refresh
              </Button>
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-6">
            <HeroStat
              icon={<Clock className="h-3.5 w-3.5" />}
              label="Pending"
              value={totals?.pending ?? 0}
              loading={isLoading}
            />
            <HeroStat
              icon={<AlertTriangle className="h-3.5 w-3.5" />}
              label="Above threshold"
              value={totals?.escalated ?? 0}
              loading={isLoading}
              accent={(totals?.escalated || 0) > 0 ? "amber" : undefined}
            />
            <HeroStat
              icon={<CircleDollarSign className="h-3.5 w-3.5" />}
              label="Total value"
              value={formatGHS(totals?.totalAmount || 0)}
              loading={isLoading}
              isText
            />
            <HeroStat
              icon={<CheckCircle2 className="h-3.5 w-3.5" />}
              label="Awaiting my approval"
              value={totals?.requiresMyApproval ?? 0}
              loading={isLoading}
              accent={
                (totals?.requiresMyApproval || 0) > 0 ? "green" : undefined
              }
            />
          </div>
        </div>
      </div>

      {/* ─── Body ─── */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Filters */}
        <div className="rounded-lg border bg-card p-4 mb-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-1 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground">
                Filter
              </span>
            </div>
            <Select
              value={businessFilter || "__all__"}
              onValueChange={(v) => setBusinessFilter(v === "__all__" ? "" : v)}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All businesses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All businesses</SelectItem>
                {businesses.map((b) => (
                  <SelectItem key={b._id} value={b._id}>
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Checkbox
                id="escalated-only"
                checked={escalatedOnly}
                onCheckedChange={(v) => setEscalatedOnly(Boolean(v))}
              />
              <Label
                htmlFor="escalated-only"
                className="text-sm font-normal cursor-pointer"
              >
                Above-threshold only
              </Label>
            </div>
          </div>
          {(businessFilter || escalatedOnly) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setBusinessFilter("");
                setEscalatedOnly(false);
              }}
            >
              Clear
            </Button>
          )}
        </div>

        {/* Error */}
        {isError && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center">
            <AlertTriangle className="h-6 w-6 text-destructive mx-auto mb-2" />
            <p className="text-sm">Couldn't load approval queue.</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              className="mt-3"
            >
              Retry
            </Button>
          </div>
        )}

        {/* Loading */}
        {isLoading && !data && (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-32 rounded-lg border bg-card animate-pulse"
              />
            ))}
          </div>
        )}

        {/* Empty */}
        {!isLoading && !isError && items.length === 0 && (
          <div className="rounded-lg border bg-card p-12 text-center">
            <CheckCircle2 className="h-10 w-10 text-[#4CAF50] mx-auto mb-3" />
            <p className="text-base font-medium">All caught up</p>
            <p className="text-sm text-muted-foreground mt-1">
              No pending approvals in your queue.
            </p>
          </div>
        )}

        {/* List */}
        {!isLoading && !isError && items.length > 0 && (
          <div className="space-y-3">
            {items.map((item) => (
              <ApprovalCard
                key={item.expenditure._id}
                item={item}
                threshold={threshold}
                onApprove={() => onApprove(item)}
                onReject={() => setRejectTarget(item)}
                approving={
                  approve.isPending &&
                  approve.variables === item.expenditure._id
                }
              />
            ))}
          </div>
        )}
      </div>

      {/* ─── Reject dialog ─── */}
      <Dialog
        open={Boolean(rejectTarget)}
        onOpenChange={(o) => {
          if (!o) {
            setRejectTarget(null);
            setRejectReason("");
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject expenditure</DialogTitle>
            <DialogDescription>
              {rejectTarget && (
                <>
                  {rejectTarget.businessName} ·{" "}
                  {formatGHS(rejectTarget.expenditure.amount)} ·{" "}
                  {rejectTarget.expenditure.description || "(no description)"}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reject-reason">Reason</Label>
            <Textarea
              id="reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Explain why this is being rejected. The submitter will see this."
              rows={4}
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              Submitter gets a notification with this reason attached.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setRejectTarget(null);
                setRejectReason("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={onRejectSubmit}
              disabled={!rejectReason.trim() || reject.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {reject.isPending ? "Rejecting…" : "Confirm rejection"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Threshold dialog ─── */}
      <Dialog open={thresholdOpen} onOpenChange={setThresholdOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Approval threshold</DialogTitle>
            <DialogDescription>
              Expenditures at or above this amount require tenant owner or super
              admin approval. Below it, any business admin in the relevant
              business can approve.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="threshold-input">Amount ({curSymbol()})</Label>
            <Input
              id="threshold-input"
              type="number"
              min="0"
              step="100"
              value={thresholdInput}
              onChange={(e) => setThresholdInput(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Currently {formatGHS(threshold)}. Set to 0 to require tenant-level
              approval on every expenditure.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setThresholdOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={onSaveThreshold}
              disabled={setThreshold.isPending}
              className="bg-[#4CAF50] hover:bg-[#45A049] text-white"
            >
              {setThreshold.isPending ? "Saving…" : "Save threshold"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Hero stat tile ────────────────────────────────────────────────

function HeroStat({
  icon,
  label,
  value,
  loading,
  accent,
  isText,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  loading?: boolean;
  accent?: "green" | "amber";
  isText?: boolean;
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-[#A0BBCB]">
        {icon}
        {label}
      </div>
      <div
        className={`mt-1.5 ${isText ? "text-xl" : "text-3xl"} font-bold ${
          accent === "amber"
            ? "text-amber-300"
            : accent === "green"
              ? "text-[#7CE08C]"
              : "text-white"
        } ${!isText ? "tabular-nums" : ""}`}
      >
        {loading ? (
          <span className="inline-block h-9 w-24 bg-white/15 rounded animate-pulse" />
        ) : (
          value
        )}
      </div>
    </div>
  );
}

// ─── Approval card ─────────────────────────────────────────────────

function ApprovalCard({
  item,
  threshold,
  onApprove,
  onReject,
  approving,
}: {
  item: ApprovalQueueItem;
  threshold: number;
  onApprove: () => void;
  onReject: () => void;
  approving?: boolean;
}) {
  const { expenditure, businessName, submitterName, requiresEscalation } = item;
  const attachmentCount = expenditure.attachments?.length || 0;

  return (
    <div
      className={`rounded-lg border bg-card transition-shadow hover:shadow-sm ${
        requiresEscalation ? "border-amber-300/60" : "border-border"
      }`}
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          {/* Left: info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge
                variant="outline"
                className="font-normal text-xs gap-1 border-[#0A2540]/30"
              >
                <Building2 className="h-3 w-3" />
                {businessName}
              </Badge>
              {requiresEscalation && (
                <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border border-amber-300 gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Above threshold · CFO approval
                </Badge>
              )}
              {expenditure.category && (
                <Badge variant="secondary" className="text-xs font-normal">
                  {expenditure.category}
                </Badge>
              )}
              {attachmentCount > 0 && (
                <Badge
                  variant="secondary"
                  className="text-xs font-normal gap-1"
                >
                  <Paperclip className="h-3 w-3" />
                  {attachmentCount}
                </Badge>
              )}
            </div>

            <p className="mt-2 text-sm font-medium text-foreground truncate">
              {expenditure.description || (
                <span className="italic text-muted-foreground">
                  No description
                </span>
              )}
            </p>

            <div className="mt-1.5 flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
              <span className="inline-flex items-center gap-1">
                <Receipt className="h-3 w-3" />
                {expenditure.referenceNumber || expenditure._id.slice(-6)}
              </span>
              <span>Submitted by {submitterName}</span>
              <span>·</span>
              <span>
                {relativeDate(expenditure.submittedAt || expenditure.createdAt)}
              </span>
            </div>
          </div>

          {/* Right: amount + actions */}
          <div className="flex flex-col items-end gap-3 shrink-0">
            <div className="text-right">
              <div
                className={`text-2xl font-bold tabular-nums ${
                  requiresEscalation ? "text-amber-700" : "text-[#0A2540]"
                }`}
              >
                {formatGHS(expenditure.amount)}
              </div>
              {requiresEscalation && (
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  threshold: {formatGHS(threshold)}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              {item.canReject && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onReject}
                  className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                >
                  Reject
                </Button>
              )}
              {item.canApprove ? (
                <Button
                  size="sm"
                  onClick={onApprove}
                  disabled={approving}
                  className="bg-[#4CAF50] hover:bg-[#45A049] text-white"
                >
                  {approving ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      Approving…
                    </>
                  ) : (
                    "Approve"
                  )}
                </Button>
              ) : (
                <Badge
                  variant="outline"
                  className="text-xs font-normal text-muted-foreground border-muted-foreground/30"
                >
                  Awaiting CFO
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
